import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSisConfig } from '../../contexts/SisConfigUi';
import SqlexecMasterDetail from '../sqlexec/SqlexecMasterDetail';
import { secureFetch } from '../../utils/authInterceptor';

const ExportarToolbar = ({ selectedRow, config, btnBase = {}, getButtonConfig, onConfirma, disabled = false }) => {
  const { modales_ui, botones_ui } = useSisConfig();
  const dropdownUI = modales_ui?.dropdown || {};

  // ── Estado dropdown ────────────────────────────────────────
  const [showMenu, setShowMenu]       = useState(false);
  const [loadingKey, setLoadingKey]   = useState(null);
  const [pendingOpcion, setPendingOpcion] = useState(null);
  const menuRef                       = useRef(null);
  const btnRef                        = useRef(null);

  // ── Cerrar dropdown al click fuera ────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current  && !btnRef.current.contains(e.target)
      ) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Guardia: si no hay config activa no renderiza ─────────
  if (!config?.active) return null;

  // ── Estilos del botón desde BD (igual que los demás botones)
  const btnConfig  = getButtonConfig ? getButtonConfig(config.color || 'gray') : {};
  const iconSize   = 16;

  // ── Icono dinámico por key de opción ──────────────────────
  const getOpcionIcon = (key) => {
    const icons = {
      export_pdf:   { svg: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8', color: '#ef4444' },
      export_excel: { svg: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M8 13l2 2 4-4 M8 17l2 2 4-4', color: '#16a34a' },
      send_email:   { svg: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6',   color: '#2563eb' },
    };
    return icons[key] || { svg: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3', color: '#6b7280' };
  };

  // ── Click botón principal ─────────────────────────────────
  const handleClick = () => {
    if (!selectedRow) {
      toast.warning('Selecciona un registro primero');
      return;
    }
    setShowMenu(prev => !prev);
  };

  // ── Ejecutar opción del dropdown ──────────────────────────
  const handleOpcion = async (opcion) => {
    setShowMenu(false);

    if (!opcion.idreporte) {
      toast.error('No hay reporte configurado para esta opción');
      return;
    }

    if (opcion.confirma) {
      setPendingOpcion(opcion);
      return;
    }

    // 🔥 Label dinámico según formato — AQUÍ va
    const fmtLabel = opcion.key === 'export_excel' ? 'Excel' : 'PDF';

    const toastId = toast.info(`Generando ${fmtLabel}, un momento...`, {
      autoClose: false
    });

    try {
      setLoadingKey(opcion.key);

      const token    = sessionStorage.getItem('mapasac_access_token');
      const response = await secureFetch('/api/sqlexec_generico/export/reporte', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          idreporte: opcion.idreporte,
          id:        selectedRow,
          formato:   opcion.key === 'export_pdf' ? 'pdf' : 
                      opcion.key === 'export_excel' ? 'excel' : 'pdf'
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Error generando ${fmtLabel}`);
      }

      // PDF: el backend devuelve la URL de vista previa con nombre (/export/ver);
      // Excel sigue como blob.
      if (opcion.key !== 'export_excel') {
        const j = await response.json();
        if (!j?.data?.url) throw new Error(`Error generando ${fmtLabel}`);
        window.open(j.data.url);
      } else {
        const blob = await response.blob();
        const url  = URL.createObjectURL(blob);
        window.open(url);
      }

      toast.update(toastId, {
        type:     'success',
        render:   `${fmtLabel} generado correctamente..!`,
        autoClose: 1000
      });

    } catch (err) {
      console.error(`❌ ExportarToolbar ${fmtLabel}:`, err);
      toast.update(toastId, {
        type:     'error',
        render:   `${err.message} — revisa configuración`,
        autoClose: 4000
      });
    } finally {
      setLoadingKey(null);
    };
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>

      {/* ── Botón principal Exportar ── */}
      <button
        ref={btnRef}
        onClick={handleClick}
        disabled={!!loadingKey || disabled}
        className="transition-colors"
        style={{
          height:           btnBase.height          || '32px',
          fontSize:         btnBase.fontSize         || '0.75rem',
          fontFamily:       btnBase.fontFamily        || 'inherit',
          fontWeight:       btnBase.fontWeight        || '500',
          paddingLeft:      btnBase.padding?.x        || '1rem',
          paddingRight:     btnBase.padding?.x        || '1rem',
          paddingTop:       btnBase.padding?.y        || '0.5rem',
          paddingBottom:    btnBase.padding?.y        || '0.5rem',
          borderRadius:     btnBase.borderRadius      || '6px',
          borderWidth:      btnBase.borderWidth       || '1px',
          borderStyle:      'solid',
          borderColor:      (loadingKey || disabled)
                              ? (btnConfig.disabledBorderColor     || '#9ca3af')
                              : (btnConfig.borderColor             || '#6b7280'),
          backgroundColor:  (loadingKey || disabled)
                              ? (btnConfig.disabledBackgroundColor || '#9ca3af')
                              : (btnConfig.backgroundColor         || '#6b7280'),
          color:            (loadingKey || disabled)
                              ? (btnConfig.disabledTextColor       || '#ffffff')
                              : (btnConfig.textColor               || '#ffffff'),
          cursor:           (loadingKey || disabled)
                               ? 'not-allowed' 
                               : 'pointer',
          display:          'flex',
          alignItems:       'center',
          justifyContent:   'center',
          gap:              '0.5rem',
          transitionDuration: btnBase.transitionDuration || '150ms',
        }}
        onMouseEnter={(e) => {
          if (!loadingKey && !disabled) {
            e.currentTarget.style.backgroundColor = btnConfig.hoverBackgroundColor || '#4b5563';
            e.currentTarget.style.borderColor      = btnConfig.hoverBorderColor     || '#4b5563';
            e.currentTarget.style.color            = btnConfig.hoverTextColor       || '#ffffff';
          }
        }}
        onMouseLeave={(e) => {
          if (!loadingKey && !disabled) {
            e.currentTarget.style.backgroundColor = btnConfig.backgroundColor || '#6b7280';
            e.currentTarget.style.borderColor      = btnConfig.borderColor     || '#6b7280';
            e.currentTarget.style.color            = btnConfig.textColor       || '#ffffff';
          }
        }}
        title={config.label || 'Exportar'}
      >
        {/* Icono download SVG */}
        <svg
          width={iconSize} height={iconSize}
          viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={loadingKey ? { animation: 'spin 1s linear infinite' } : {}}
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span>{config.label || 'Exportar'}</span>
      </button>

      {/* ── Dropdown opciones ── */}
      {showMenu && config.opciones?.length > 0 && (
        <div
          ref={menuRef}
          style={{
            position:        'absolute',
            top:             'calc(100% + 4px)',
            right:           0,
            minWidth:        '100px',
            backgroundColor: dropdownUI.backgroundColor || '#ffffff',
            border:          dropdownUI.border          || '1px solid #e5e7eb',
            borderRadius:    dropdownUI.borderRadius    || '8px',
            boxShadow:       dropdownUI.boxShadow       || '0 2px 8px rgba(0,0,0,0.06)',
            zIndex:          9999,
            overflow:        'hidden',
          }}
        >
          {config.opciones.map((opcion, idx) => {
            const opIcon      = getOpcionIcon(opcion.key);
            const isLoading   = loadingKey === opcion.key;

            return (
              <button
                key={opcion.key}
                onClick={() => handleOpcion(opcion)}
                disabled={isLoading}
                style={{
                  width:           '100%',
                  display:         'flex',
                  alignItems:      'center',
                  gap:             dropdownUI.itemGap        || '10px',
                  padding:         dropdownUI.itemPadding    || '9px 16px',
                  fontSize:        dropdownUI.itemFontSize   || btnBase.fontSize || '0.78rem',
                  fontFamily:      btnBase.fontFamily        || 'inherit',
                  color:           isLoading 
                                    ? (dropdownUI.itemDisabledColor || '#9ca3af') 
                                    : (dropdownUI.itemColor         || '#374151'),
                  backgroundColor: dropdownUI.backgroundColor || '#ffffff',
                  border:          'none',
                  borderTop:       idx > 0 ? (dropdownUI.itemBorderTop || '1px solid #f3f4f6') : 'none',
                  cursor:          isLoading ? 'not-allowed' : 'pointer',
                  textAlign:       'left',
                  transition:      `background-color ${btnBase.transitionDuration || '120ms'}`,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = dropdownUI.itemHoverBackground || '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = dropdownUI.backgroundColor || '#ffffff';
                }}
              >
                {/* Icono SVG de la opción */}
                <svg
                  width="15" height="15"
                  viewBox="0 0 24 24" fill="none"
                  stroke={isLoading ? '#9ca3af' : opIcon.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {opIcon.svg.split(' M').map((segment, i) => (
                    <path key={i} d={i === 0 ? segment : 'M' + segment} />
                  ))}
                </svg>
                <span>{opcion.label}</span>
                {isLoading && (
                  <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#9ca3af' }}>
                    ...
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {pendingOpcion && (
        <SqlexecMasterDetail
          key="confirm-export"
          componentForm="Sys_Confirma_Mensaje"
          mode="create"
          useGenerico={true}
          initialData={{
            idconfirma: 1,
            mode:   pendingOpcion.confirma_mode || 'export_catalogo',
            titulo: pendingOpcion.label || '',
          }}
          onClose={() => setPendingOpcion(null)}
          onSuccess={() => {
            const opcion = { ...pendingOpcion, confirma: false };
            setPendingOpcion(null);
            handleOpcion(opcion);
          }}
        />
      )}

    </div>
  );
};

export default ExportarToolbar;